import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Autocomplete,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
  Menu,
  MenuItem,
  Box,
} from "@mui/material";
import {
  DataGrid,
  GridPagination,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MdOutlineClear,
  MdOutlineInfo,
  MdCheckCircleOutline,
  MdCancel,
  MdMoreVert,
  MdReceiptLong,
  MdPrint,
} from "react-icons/md";
import dayjs from "dayjs";
import {
  BRAND,
  MobileAction,
  MobileBadge,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  TONE,
} from "../../../../../components/MobileList";
import {
  EpaymentTransactions,
  setSearchBody,
} from "../../../../../app/slices/epaymentTransactionsSlice";
import { SelectRepresentative } from "../../../../../components/Selectors/SelectRepresentative";
import { SelectTeamLeader } from "../../../../../components/Selectors/SelectTeamLeader";
import { SelectPos } from "../../../../../components/Selectors/SelectPos";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import { BsFillFileEarmarkExcelFill } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import ConfirmationDialog from "../../../../../helpers/ConfirmationDialog";
import { restoreFilters } from "../../../../../helpers/restoreFilters";
import { showNotification } from "../../../../../app/slices/notificationSlice";
import TransactionPrintDialog from "./TransactionPrintDialog";
import UserInfoCard from "../../../../wallets/transactions/dejlah/userInfoCard";
import FinancialInfoCard from "../../../../wallets/transactions/dejlah/financialInfoCard";

const EpaymentTransactionsTable = ({ posId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [actionsAnchor, setActionsAnchor] = useState({ el: null, row: null });

  // When rendered inside a single POS context (e.g. wallet transactions page),
  // the table is locked to that POS and the POS/rep selectors are hidden.
  const scopedToPos = Boolean(posId);

  const _EpaymentTransactions = useSelector(
    (state) => state.epaymentTransactions.epaymentTransactions,
  );
  const _SearchBody = useSelector(
    (state) => state.epaymentTransactions.searchBody,
  );
  const users = useSelector((state) => state.user.usersList.data);
  const _TeamLeader = useSelector((state) => state.team.leaders);
  const _PointOfsales = useSelector(
    (state) => state.pointOfsales.posesList.data,
  );
  const _Role = useSelector((state) => state.user.userInfo.user_roles[0]);
  const userPermissions = useSelector(
    (state) => state.user.userInfo.permissions,
  );

  const [search, setSearch] = useState(() => restoreFilters({
    order_id: "",
    pos_id: posId ?? null,
    rep_id: null,
    team_leader_id: null,
    status: null,
    start_date: null,
    end_date: null,
    pos_number: "",
    other_id: "",
    e_payment_fees: "",
    is_app: null,
  }, {
    ..._SearchBody,
    pos_id: posId ?? _SearchBody.pos_id,
    // store keeps the fee divided by 100
    e_payment_fees:
      _SearchBody.e_payment_fees === undefined ||
      _SearchBody.e_payment_fees === ""
        ? ""
        : _SearchBody.e_payment_fees * 100,
  }));
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [dialogData, setDialogData] = useState({ row: null, status: null });
  const [printRow, setPrintRow] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const posNumberFromParams = queryParams.get("pos_number");
    if (posNumberFromParams) {
      setSearch((prevSearch) => ({
        ...prevSearch,
        pos_number: posNumberFromParams,
      }));
    }
  }, [location.search]);

  const status = [
    {
      status: "Success",
      key: "success",
    },
    {
      status: "Failed",
      key: "failed",
    },
    {
      status: "Need Confirmation",
      key: "need_confirmation",
    },
  ];

  const handleManualConfirm = (row, status) => {
    setDialogData({ row, status });
    setOpenConfirm(true);
  };

  const handleConfirmManual = (password) => {
    // Check password if required
    if (
      (dialogData.status === "success" || dialogData.status === "failed") &&
      password !== "132479"
    ) {
      dispatch(
        showNotification({
          message: "كلمة المرور غير صحيحة",
          type: "error",
        }),
      );
      return;
    }
    if (dialogData.row && dialogData.status) {
      dispatch(
        EpaymentTransactions.manualConfirmApp2app(
          {
            epaymentId: dialogData.row.id,
            status: dialogData.status,
            response: dialogData.row.transaction_id || "",
          },
          _SearchBody,
        ),
      );
    }
    setOpenConfirm(false);
    setDialogData({ row: null, status: null });
  };

  const handleCancelManual = () => {
    setOpenConfirm(false);
    setDialogData({ row: null, status: null });
  };

  const columns = [
    {
      field: "sequence",
      headerName: "#",
      flex: 0.5,
    },
    {
      field: "order_id",
      headerName: "رقم الطلب",
      flex: 1,
    },
    {
      field: "type",
      headerName: "النوع",
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          <Typography>
            {params.row.e_payment_type} -{" "}
            {params.row.from_app2app ? "App2App" : "Web"}
          </Typography>
          {/* <Typography>{params.row.from_app2app ? "App2App" : "Web"}</Typography> */}
        </Stack>
      ),
    },
    {
      field: "amount",
      headerName: "المبلغ (IQD)",
      flex: 1,
      renderCell: (params) => parseInt(params.row.amount)?.toLocaleString(),
    },
    {
      field: "e_balance",
      headerName: "الرصيد قبل/بعد العملية (IQD)",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            قبل:{" "}
            {params.row.before_e_balance != null
              ? parseInt(params.row.before_e_balance).toLocaleString()
              : "-"}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            بعد:{" "}
            {params.row.after_e_balance != null
              ? parseInt(params.row.after_e_balance).toLocaleString()
              : "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "gateway",
      headerName: "البوابة",
      flex: 1,
    },
    {
      field: "pos_name",
      headerName: "نقطة البيع",
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="a"
          href={`/app/wallets/transactions/${params.row.user_id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.preventDefault();
            localStorage.setItem("isWholeSale", "0");
            window.open(
              `/app/wallets/transactions/${params.row.user_id}`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
        >
          {params.row.pos_name}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "الحالة",
      flex: 1,
      renderCell: (params) => (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="body">{params.row.status}</Typography>
            <Tooltip title={params.row.failed_details ?? ""}>
              <Typography variant="body">
                {params.row.status === "failed" ? (
                  <MdOutlineInfo size={22} />
                ) : null}
              </Typography>
            </Tooltip>
          </Stack>
          {params.row.success_details === "manual confirmation" && (
            <Tooltip
              title={dayjs(params.row.approval_date).format(
                "YYYY-MM-DD hh:mm a",
              )}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                تأكيد يدوي:{" "}
                {dayjs(params.row.approval_date).format("YYYY-MM-DD hh:mm a")}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <Typography>
            {dayjs(params.row.created_at).format("YYYY-MM-DD")}
          </Typography>
          <Typography variant="caption">
            {dayjs(params.row.created_at).format("hh:mm a")}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "الإجراءات",
      width: 100,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            icon={<MdPrint />}
            label="طباعة"
            onClick={() => setPrintRow(params.row)}
          />,
        ];
        if (
          params.row.status === "need_confirmation" &&
          (_Role === "super_admin" ||
            (Array.isArray(userPermissions) &&
              userPermissions
                .map((data) => data.name)
                .includes("e_payment_update")))
        ) {
          actions.push(
            <GridActionsCellItem
              icon={<MdCheckCircleOutline color="green" />}
              label="تأكيد نجاح"
              onClick={() => handleManualConfirm(params.row, "success")}
              showInMenu={true}
            />,
            <GridActionsCellItem
              icon={<MdCancel color="red" />}
              label="تأكيد فشل"
              onClick={() => handleManualConfirm(params.row, "failed")}
              showInMenu={true}
            />,
          );
        }
        return actions;
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(EpaymentTransactions.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        order_id: search.order_id ?? "",
        status: search.status ?? "",
        rep_id: search.rep_id ?? "",
        team_leader_id: search.team_leader_id ?? "",
        pos_id: posId ?? search.pos_id ?? "",
        start_date: search.start_date ?? "",
        end_date: search.end_date ?? "",
        pos_number: search.pos_number ?? "",
        other_id: search.other_id ?? "",
        e_payment_fees:
          search.e_payment_fees === "" ? "" : search.e_payment_fees / 100,
        is_app: search.is_app ?? null,
        skip: currentPage * pageSize,
        take: pageSize,
      }),
    );
  }, [currentPage, dispatch, pageSize, search, posId]);

  const statusColor = (s) => {
    if (s === "success")
      return { bg: "#E8F5E9", color: "#2E7D32", label: "ناجحة" };
    if (s === "failed")
      return { bg: "#FFEBEE", color: "#C62828", label: "فاشلة" };
    if (s === "need_confirmation")
      return { bg: "#FFF8E1", color: "#B26A00", label: "بانتظار التأكيد" };
    return { bg: "#ECEFF1", color: "#455A64", label: s };
  };

  const canManualConfirm = (row) =>
    row.from_app2app &&
    row.status === "need_confirmation" &&
    (_Role === "super_admin" ||
      (Array.isArray(userPermissions) &&
        userPermissions.map((d) => d.name).includes("e_payment_update")));

  const totalPages = Math.max(
    1,
    Math.ceil((_EpaymentTransactions.total || 0) / pageSize),
  );

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {_EpaymentTransactions.total_amount.toLocaleString()}{" "}
          (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          {/* Only in the single-POS context (wallet transactions page), where
              the user data this card reads is loaded. */}
          {scopedToPos && (
            <Grid item xs={12}>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <UserInfoCard />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FinancialInfoCard />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              value={search.search}
              onChange={(e) => {
                setSearch({
                  ...search,
                  order_id: e.target.value,
                });
              }}
              label="رقم الطلب"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={status}
              getOptionLabel={(option) => option.status || ""}
              value={status.filter((x) => x.key === search.status)[0] ?? null}
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  status: newValue ? newValue.key : null,
                });
              }}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField {...params} label="الحالة" size="small" />
              )}
            />
          </Grid>
          {!scopedToPos && (
            <Grid item xs={12} md={3}>
              <SelectRepresentative
                onChange={(e, value) => {
                  setSearch({
                    ...search,
                    rep_id: value ? value.id : "",
                  });
                }}
                value={
                  users.filter((user) => user.id === search.rep_id)[0] ?? null
                }
              />
            </Grid>
          )}
          {!scopedToPos && (
            <Grid item xs={12} md={3}>
              <SelectTeamLeader
                onChange={(e, value) => {
                  setSearch({
                    ...search,
                    team_leader_id: value ? value.id : null,
                  });
                }}
                value={
                  _TeamLeader.data?.filter(
                    (x) => x.id === search.team_leader_id,
                  )[0] ?? null
                }
              />
            </Grid>
          )}
          {!scopedToPos && (
            <Grid item xs={12} md={3}>
              <SelectPos
                onChange={(event, value) => {
                  setSearch({
                    ...search,
                    pos_id: value ? value.id : null,
                  });
                }}
                value={
                  _PointOfsales?.filter((x) => search.pos_id === x.id)[0] ??
                  null
                }
              />
            </Grid>
          )}
          {!scopedToPos && (
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                value={search.pos_number}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    pos_number: e.target.value,
                  });
                }}
                label="رقم نقطة البيع"
                type="number"
                fullWidth
              />
            </Grid>
          )}
          {!scopedToPos && (
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                value={search.other_id}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    other_id: e.target.value,
                  });
                }}
                label="معرف النقطة"
                type="number"
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              value={search.e_payment_fees}
              onChange={(e) => {
                setSearch({
                  ...search,
                  e_payment_fees: e.target.value,
                });
              }}
              label="نسبة العمولة"
              type="number"
              inputProps={{ step: "any" }}
              helperText="ادخل النسبة المئوية مثال 3.5 تعني 3.5%"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={[
                { label: "App2App", value: 1 },
                { label: "Web", value: 0 },
              ]}
              getOptionLabel={(option) => option.label || ""}
              value={
                search.is_app !== null
                  ? [
                    { label: "App2App", value: 1 },
                    { label: "Web", value: 0 },
                  ].find((x) => x.value === search.is_app) || null
                  : null
              }
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  is_app: newValue ? newValue.value : null,
                });
              }}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} label="نوع المعاملة" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateRangepicker
              startDate={search.start_date}
              endDate={search.end_date}
              handleOnChange={(searchDate) => {
                const updatedFilter = {
                  from: dayjs(searchDate[0].startDate).format("YYYY-MM-DD"),
                  to: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                };
                if (
                  updatedFilter.from !== searchDate.startDate ||
                  updatedFilter.to !== searchDate.endDate
                ) {
                  setSearch({
                    ...search,
                    start_date: dayjs(searchDate[0].startDate).format(
                      "YYYY-MM-DD",
                    ),
                    end_date: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Stack
              direction="row"
              spacing={0}
              sx={{ alignEpaymentTransactions: "center" }}
            >
              <Tooltip title="تصدير ملف اكسل">
                <IconButton
                  onClick={() => {
                    dispatch(
                      EpaymentTransactions.epaymentTransactionsExportExcel(
                        _SearchBody,
                      ),
                    );
                  }}
                >
                  <BsFillFileEarmarkExcelFill />
                </IconButton>
              </Tooltip>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch({
                      search: "",
                      status: null,
                      order_id: null,
                      rep_id: null,
                      team_leader_id: null,
                      pos_id: posId ?? null,
                      start_date: null,
                      end_date: null,
                      pos_number: "",
                      other_id: "",
                      e_payment_fees: "",
                      is_app: null,
                      has_e_payment: null,
                    });
                    setPageSize(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              {scopedToPos
                ? "رصيد محفظة الدفع الالكتروني"
                : "مجموع أرصدة محافظ الدفع الالكتروني"}{" "}
              : {(_EpaymentTransactions.total_debt ?? 0).toLocaleString()} (IQD)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {isMobile ? (
              <Box>
                <MobileListShell loading={_EpaymentTransactions.loading}>
                  {_EpaymentTransactions.data.length === 0 &&
                    !_EpaymentTransactions.loading ? (
                    <MobileEmpty />
                  ) : (
                    _EpaymentTransactions.data.map((row, idx) => {
                      const sc = statusColor(row.status);
                      return (
                        <MobileListItem
                          key={row.id ?? idx}
                          avatar={<MdReceiptLong />}
                          avatarTone={sc.color}
                          title={`#${row.order_id}`}
                          subtitle={`${row.e_payment_type} · ${row.from_app2app ? "App2App" : "Web"
                            }${row.gateway ? ` · ${row.gateway}` : ""}`}
                          badge={
                            row.status === "failed" && row.failed_details ? (
                              <Tooltip title={row.failed_details}>
                                <Box>
                                  <MobileBadge
                                    label={sc.label}
                                    tone={sc.color}
                                  />
                                </Box>
                              </Tooltip>
                            ) : (
                              <MobileBadge label={sc.label} tone={sc.color} />
                            )
                          }
                          highlight={{
                            label: "المبلغ",
                            value: parseInt(row.amount ?? 0)?.toLocaleString(),
                            unit: "IQD",
                          }}
                          fields={[
                            {
                              label: "نقطة البيع",
                              span: 12,
                              value: (
                                <Box
                                  component="span"
                                  sx={{
                                    color: BRAND,
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    localStorage.setItem("isWholeSale", "0");
                                    window.open(
                                      `/app/wallets/transactions/${row.user_id}`,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                >
                                  {row.pos_name}
                                </Box>
                              ),
                            },
                            {
                              label: "الرصيد قبل",
                              value:
                                row.before_e_balance != null
                                  ? parseInt(
                                    row.before_e_balance,
                                  ).toLocaleString()
                                  : null,
                            },
                            {
                              label: "الرصيد بعد",
                              value:
                                row.after_e_balance != null
                                  ? parseInt(
                                    row.after_e_balance,
                                  ).toLocaleString()
                                  : null,
                            },
                            {
                              label: "تاريخ العملية",
                              span: 12,
                              value: dayjs(row.created_at).format(
                                "YYYY-MM-DD hh:mm a",
                              ),
                            },
                          ]}
                          actions={
                            <>
                              <MobileAction
                                title="طباعة"
                                tone={TONE.neutral}
                                icon={<MdPrint />}
                                onClick={() => setPrintRow(row)}
                              />
                              {canManualConfirm(row) && (
                                <MobileAction
                                  title="الخيارات"
                                  icon={<MdMoreVert />}
                                  onClick={(e) =>
                                    setActionsAnchor({
                                      el: e.currentTarget,
                                      row,
                                    })
                                  }
                                />
                              )}
                            </>
                          }
                        />
                      );
                    })
                  )}
                </MobileListShell>

                {_EpaymentTransactions.total > 0 && (
                  <Stack alignItems="center" spacing={1} sx={{ mt: 2, pb: 1 }}>
                    <Typography variant="caption" color="primary">
                      المجموع:{" "}
                      {_EpaymentTransactions.total_amount.toLocaleString()} IQD
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage + 1}
                      onChange={(e, p) => setCurrentPage(p - 1)}
                      color="primary"
                      size="small"
                      siblingCount={0}
                    />
                  </Stack>
                )}

                <Menu
                  anchorEl={actionsAnchor.el}
                  open={Boolean(actionsAnchor.el)}
                  onClose={() => setActionsAnchor({ el: null, row: null })}
                >
                  <MenuItem
                    onClick={() => {
                      handleManualConfirm(actionsAnchor.row, "success");
                      setActionsAnchor({ el: null, row: null });
                    }}
                  >
                    <MdCheckCircleOutline
                      color="green"
                      style={{ marginInlineEnd: 8 }}
                    />
                    تأكيد نجاح
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleManualConfirm(actionsAnchor.row, "failed");
                      setActionsAnchor({ el: null, row: null });
                    }}
                  >
                    <MdCancel color="red" style={{ marginInlineEnd: 8 }} />
                    تأكيد فشل
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <DataGrid
                rows={_EpaymentTransactions.data.map((x) => {
                  return {
                    ...x,
                    sequence: _EpaymentTransactions.data.indexOf(x) + 1,
                  };
                })}
                autoHeight
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowCount={_EpaymentTransactions.total}
                loading={_EpaymentTransactions.loading}
                rowHeight={50}
                pagination
                paginationMode="server"
                onPageChange={(newPage) => {
                  setCurrentPage(newPage);
                }}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                }}
                components={{
                  LoadingOverlay: LinearProgress,
                  Footer: CustomFooter,
                }}
                disableSelectionOnClick
              />
            )}
          </Grid>
        </Grid>
        <ConfirmationDialog
          open={openConfirm}
          title="تأكيد"
          message={`هل انت متأكد من تأكيد العملية كـ "${dialogData.status === "success"
              ? "ناجحة"
              : dialogData.status === "failed"
                ? "فاشلة"
                : ""
            }"؟`}
          name={dialogData.row?.order_id || ""}
          confirmText="تأكيد"
          cancelText="إلغاء"
          onConfirm={handleConfirmManual}
          onCancel={handleCancelManual}
          requirePassword={
            dialogData.status === "success" || dialogData.status === "failed"
          }
          passwordLabel="كلمة المرور"
        />
        <TransactionPrintDialog
          open={Boolean(printRow)}
          transaction={printRow}
          onClose={() => setPrintRow(null)}
        />
      </CardContent>
    </Card>
  );
};

export default EpaymentTransactionsTable;
